/* Wrist-X native product page interactivity. Scoped to custom elements so
   multiple instances stay independent and other sections are unaffected. */

/* Use-cases selector: tabs, dots, arrows and swipe drive one shared card.
   Below 1300px (matches the CSS breakpoint) the layout is the stacked
   carousel; desktop uses the vertical tab column only. */
if (!customElements.get('wristx-use-cases')) {
  customElements.define(
    'wristx-use-cases',
    class WristxUseCases extends HTMLElement {
      connectedCallback() {
        var dataNode = this.querySelector('[data-wxuc-data]');
        if (!dataNode) return;
        try {
          this.data = JSON.parse(dataNode.textContent);
        } catch (e) {
          return;
        }
        if (!this.data || !this.data.length) return;

        this.MOBILE_BP = 1300;
        this.currentIndex = 0;

        this.tabs = Array.from(this.querySelectorAll('[data-wxuc-tab]'));
        this.dots = Array.from(this.querySelectorAll('[data-wxuc-dot]'));
        this.strip = this.querySelector('[data-wxuc-tabs]');
        this.titleEl = this.querySelector('[data-wxuc-title]');
        this.eyebrowEl = this.querySelector('[data-wxuc-eyebrow]');
        this.descEl = this.querySelector('[data-wxuc-desc]');
        this.benefitsEl = this.querySelector('[data-wxuc-benefits]');
        this.video = this.querySelector('[data-wxuc-video]');
        this.videoSource = this.querySelector('[data-wxuc-video-source]');
        this.card = this.querySelector('[data-wxuc-card]');
        this.prevArrow = this.querySelector('[data-wxuc-prev]');
        this.nextArrow = this.querySelector('[data-wxuc-next]');
        var nav = this.querySelector('[data-wxuc-nav]');

        var self = this;

        // Keep nav taps from reaching the card's swipe handler.
        if (nav) {
          ['pointerdown', 'pointerup', 'pointermove', 'touchstart', 'touchend', 'click'].forEach(function (evt) {
            nav.addEventListener(evt, function (e) {
              e.stopPropagation();
            });
          });
        }

        this.tabs.forEach(function (tab) {
          tab.addEventListener('click', function () {
            self.goToIndex(Number(tab.dataset.wxucTab));
          });
        });

        // Tapping a <button> makes the browser scroll it "into view", walking
        // up to the window — on the end tabs that drags the whole page
        // sideways. We center the active tab ourselves, so suppress the
        // native focus-scroll on pointer press; click and keyboard Tab focus
        // stay intact.
        this.tabs.concat(this.dots, [this.prevArrow, this.nextArrow]).forEach(function (el) {
          if (el) el.addEventListener('mousedown', function (e) { e.preventDefault(); });
        });

        this.dots.forEach(function (dot) {
          self.bindButton(dot, function () {
            self.goToIndex(Number(dot.dataset.wxucDot));
          });
        });
        this.bindButton(this.prevArrow, function () { self.goToIndex(self.currentIndex - 1); });
        this.bindButton(this.nextArrow, function () { self.goToIndex(self.currentIndex + 1); });

        this.bindSwipe();
        this.setActive(0, true);
      }

      // pointerup on the buttons themselves — fires before any click delay
      // and works even if something interferes with click events.
      bindButton(el, handler) {
        if (!el) return;
        var pressed = false;
        el.addEventListener('pointerdown', function (e) {
          pressed = true;
          e.stopPropagation();
        });
        el.addEventListener('pointerup', function (e) {
          e.stopPropagation();
          if (pressed) {
            pressed = false;
            handler();
          }
        });
        el.addEventListener('pointercancel', function () { pressed = false; });
        el.addEventListener('click', function (e) { e.stopPropagation(); });
      }

      bindSwipe() {
        if (!this.card) return;
        var self = this;
        var startX = 0;
        var startY = 0;
        var isPointerDown = false;

        this.card.addEventListener('pointerdown', function (e) {
          if (window.innerWidth > self.MOBILE_BP) return;
          if (e.target.closest('[data-wxuc-nav]')) return;
          isPointerDown = true;
          startX = e.clientX;
          startY = e.clientY;
          try {
            self.card.setPointerCapture(e.pointerId);
          } catch (err) { /* no-op */ }
        });

        this.card.addEventListener('pointerup', function (e) {
          if (!isPointerDown || window.innerWidth > self.MOBILE_BP) return;
          isPointerDown = false;
          var diffX = e.clientX - startX;
          var diffY = e.clientY - startY;
          if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 55) {
            self.goToIndex(diffX < 0 ? self.currentIndex + 1 : self.currentIndex - 1);
          }
          try {
            self.card.releasePointerCapture(e.pointerId);
          } catch (err) { /* no-op */ }
        });

        this.card.addEventListener('pointercancel', function () {
          isPointerDown = false;
        });
      }

      goToIndex(index) {
        if (index < 0) index = this.data.length - 1;
        if (index >= this.data.length) index = 0;
        this.currentIndex = index;
        this.setActive(index, false);
      }

      setActive(index, initial) {
        var d = this.data[index];
        if (!d) return;

        this.tabs.forEach(function (tab, i) {
          tab.classList.toggle('active', i === index);
          tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
        this.dots.forEach(function (dot, i) {
          dot.classList.toggle('active', i === index);
        });

        if (!initial) {
          if (this.titleEl) this.titleEl.textContent = d.title || '';
          if (this.eyebrowEl) this.eyebrowEl.textContent = d.eyebrow || '';
          if (this.descEl) this.descEl.textContent = d.desc || '';

          if (this.video && this.videoSource && d.video) {
            this.videoSource.src = d.video;
            this.video.load();
            this.video.play().catch(function () { /* autoplay may be blocked */ });
          }

          if (this.benefitsEl) {
            this.benefitsEl.textContent = '';
            (d.benefits || []).forEach(function (label) {
              if (!label) return;
              var row = document.createElement('div');
              row.className = 'wxuc__benefit';
              var check = document.createElement('span');
              check.className = 'wxuc__check';
              check.textContent = '✓';
              var strong = document.createElement('strong');
              strong.textContent = label;
              row.appendChild(check);
              row.appendChild(strong);
              this.benefitsEl.appendChild(row);
            }, this);
          }
        }

        this.centerTabInStrip(index);
      }

      // Scroll ONLY the tab strip — never the page. scrollIntoView() walks
      // every scrollable ancestor (including the window) and, on the end
      // tabs, scrolls the whole viewport sideways.
      centerTabInStrip(index) {
        var strip = this.strip;
        var tab = this.tabs[index];
        if (!strip || !tab) return;
        if (strip.scrollWidth <= strip.clientWidth) return; // desktop column

        var target = tab.offsetLeft - (strip.clientWidth - tab.clientWidth) / 2;
        var max = strip.scrollWidth - strip.clientWidth;
        strip.scrollTo({
          left: Math.max(0, Math.min(target, max)),
          behavior: 'smooth'
        });
      }
    }
  );
}

/* Best-sellers tab switcher. */
if (!customElements.get('wristx-best-sellers')) {
  customElements.define(
    'wristx-best-sellers',
    class WristxBestSellers extends HTMLElement {
      connectedCallback() {
        var tabs = Array.from(this.querySelectorAll('[data-wxbs-tab]'));
        var panels = Array.from(this.querySelectorAll('[data-wxbs-panel]'));
        if (tabs.length < 2 || panels.length < 2) return;

        tabs.forEach(function (tab, index) {
          tab.addEventListener('click', function () {
            tabs.forEach(function (t, i) {
              t.setAttribute('aria-selected', i === index ? 'true' : 'false');
            });
            panels.forEach(function (p, i) {
              p.hidden = i !== index;
            });
          });
        });
      }
    }
  );
}
